import React, { memo } from '../../../../lib/teact/teact';
import { withGlobal } from '../../../../global';

import {
  IS_EXPLORER,
  IS_EXTENSION,
  IS_FEATURE_LIMITED,
  SELF_UNIVERSAL_HOST_URL,
} from '../../../../config';
import { selectCurrentAccountId } from '../../../../global/selectors';
import buildClassName from '../../../../util/buildClassName';
import { tryOpenNativeApp } from '../../../../util/deeplink';
import { IS_ELECTRON } from '../../../../util/windowEnvironment';

import { useDeviceScreen } from '../../../../hooks/useDeviceScreen';
import useLang from '../../../../hooks/useLang';

import AccountSelector from './AccountSelector';
import BackButton from './actionButtons/BackButton';
import ToggleLayoutButton from './actionButtons/ToggleLayoutButton';
import ToggleSensitiveDataButton from './actionButtons/ToggleSensitiveDataButton';

import styles from './Header.module.scss';

import logoSrc from '../../../../assets/logoMinimalistic.svg';

export const HEADER_HEIGHT_REM = 3;

interface OwnProps {
  isScrolled?: boolean;
  withBalance?: boolean;
  areTabsStuck?: boolean;
}

interface StateProps {
  isSensitiveDataHidden: boolean;
  isTemporaryAccount: boolean;
}

function Header({
  withBalance,
  areTabsStuck,
  isScrolled,
  isSensitiveDataHidden,
  isTemporaryAccount,
}: OwnProps & StateProps) {
  const lang = useLang();

  const { isPortrait } = useDeviceScreen();
  const canToggleAppLayout = IS_EXTENSION || IS_ELECTRON;

  const handleOpenInAppClick = (e: React.MouseEvent) => {
    e.preventDefault();
    tryOpenNativeApp(SELF_UNIVERSAL_HOST_URL);
  };

  const showBackButton = isTemporaryAccount && !IS_EXPLORER;
  const withAccountSelectorSurface = isPortrait && !isScrolled;
  const headerClassName = buildClassName(
    styles.header,
    areTabsStuck && styles.areTabsStuck,
    isScrolled && styles.isScrolled,
  );

  if (isPortrait && IS_EXPLORER) {
    return (
      <div className={headerClassName}>
        <div className={styles.headerInner} style="--icons-amount: 3">
          <AccountSelector
            withBalance={withBalance}
            withAccountSelector={!IS_FEATURE_LIMITED && !IS_EXPLORER}
            withSurface={withAccountSelectorSurface}
          />
          <div className={styles.portraitActionsRight}>
            <a
              href={SELF_UNIVERSAL_HOST_URL}
              className={styles.openLink}
              onClick={handleOpenInAppClick}
            >
              <img src={logoSrc} alt="" className={styles.mtLogo} />
              {lang('Open')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  const showSensitiveDataButton = !isPortrait && !IS_EXPLORER;
  const buttonsAmount = Math.max(
    1,
    (showBackButton ? 1 : 0) + (showSensitiveDataButton ? 1 : 0),
    canToggleAppLayout ? 1 : 0,
  );

  const actionsStartClassName = isPortrait
    ? styles.portraitActionsLeft
    : buildClassName(
      styles.landscapeActions,
      styles.landscapeActionsStart,
      styles[`landscapeActionsButtons${buttonsAmount}`],
    );
  const actionsEndClassName = isPortrait
    ? styles.portraitActionsRight
    : buildClassName(
      styles.landscapeActions,
      styles.landscapeActionsEnd,
      buttonsAmount > 1 && styles[`landscapeActionsButtons${buttonsAmount}`],
    );

  return (
    <div className={headerClassName}>
      <div className={styles.headerInner} style={`--icons-amount: ${buttonsAmount}`}>
        <div className={actionsStartClassName}>
          {showBackButton && <BackButton isIconOnly />}
          {showSensitiveDataButton && (
            <ToggleSensitiveDataButton isSensitiveDataHidden={isSensitiveDataHidden} />
          )}
        </div>

        <AccountSelector
          withBalance={withBalance}
          withAccountSelector={!IS_FEATURE_LIMITED && !IS_EXPLORER}
          withSurface={withAccountSelectorSurface}
        />

        <div className={actionsEndClassName}>
          {canToggleAppLayout && <ToggleLayoutButton />}
        </div>
      </div>
    </div>
  );
}

export default memo(withGlobal<OwnProps>(
  (global): StateProps => {
    const {
      currentTemporaryViewAccountId,
      settings: {
        isSensitiveDataHidden,
      },
    } = global;

    return {
      isSensitiveDataHidden: Boolean(isSensitiveDataHidden),
      isTemporaryAccount: Boolean(currentTemporaryViewAccountId),
    };
  },
  (global, _, stickToFirst) => stickToFirst(selectCurrentAccountId(global)),
)(Header));
